import { useAppStore } from '@store'
import { ReactMouseEvent, SetState, TexturedSceneObjects, Textures, TexturesGroup } from '@types'
import { BackAcabadosInfo } from '@utils/conversion/getStandardMaterial'
import {
  MutableRefObject,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import handleCopy from './utils/sceneobjects/handleCopy'
import handleGroupClick from './utils/sceneobjects/handleGroupClick'
import handlePaste from './utils/sceneobjects/handlePaste'
import { handlePasteAll } from './utils/sceneobjects/handlePasteAll'
import handleTextureClick from './utils/sceneobjects/handleTextureClick'
import handleZoneClick from './utils/sceneobjects/handleZoneClick'
import { PasteKeys } from './utils/shared'

type CenterInfo = TexturesGroup[]
export type ItemZoneContextType = {
  currentTexture: Textures[number] | null
  setCurrentTexture: SetState<Textures[number] | null> // Asegúrate de que esto permita null
  copiedTexture: Textures[number] | null
  setCopiedTexture: SetState<Textures[number] | null>
  copiedIconId: string
  setCopiedIconId: SetState<string>
  searchValue: string
  setSearchValue: SetState<string>
  controlledValues: number[]
  setControlledValues: SetState<number[]> // Cambié el tipo aquí para que coincida con controlledValues
  centerInfo: CenterInfo | undefined
  setCenterInfo: SetState<CenterInfo | undefined>
  rightInfo: { label: string; textures: BackAcabadosInfo[] }
  setRightInfo: SetState<{ label: string; textures: BackAcabadosInfo[] }>
  object: MutableRefObject<TexturedSceneObjects | null>
  pasteIcons: Record<string, PasteKeys>
  setPasteIcons: SetState<Record<string, PasteKeys>>
}

export const ItemZoneContext = createContext<ItemZoneContextType | undefined>(undefined)

type ItemZoneProviderProps = {
  children: ReactNode
}

export const ItemZoneProvider = ({ children }: ItemZoneProviderProps) => {
  const object = useRef<TexturedSceneObjects | null>(null)
  const closeModal = useAppStore.use.closeModal()
  const [copiedTexture, setCopiedTexture] = useState<Textures[number] | null>(null) // Copias toda la info de una textura en una zona, name,idTextura y poco más
  const [currentTexture, setCurrentTexture] = useState<Textures[number] | null>(null) // Selected es la zona seleccionada, que puede no existir
  const [searchValue, setSearchValue] = useState('') // El valor de la búsqueda
  const [centerInfo, setCenterInfo] = useState<CenterInfo>() // Esto debería ser el mainGroup?
  const [rightInfo, setRightInfo] = useState<{ label: string; textures: BackAcabadosInfo[] }>({
    label: '',
    textures: [],
  })
  const [pasteIcons, setPasteIcons] = useState<Record<string, PasteKeys>>({})
  const [controlledValues, setControlledValues] = useState<number[]>([])
  const [copiedIconId, setCopiedIconId] = useState('')
  const contextValue = {
    currentTexture,
    setCurrentTexture,
    setCopiedTexture,
    copiedTexture,
    copiedIconId,
    setCopiedIconId,
    searchValue,
    setSearchValue,
    controlledValues,
    setControlledValues,
    centerInfo,
    rightInfo,
    setCenterInfo,
    setRightInfo,
    object,
    pasteIcons,
    setPasteIcons,
  } satisfies ItemZoneContextType

  useLayoutEffect(() => {
    const unsubscribe = useAppStore.subscribe((s) => {
      const coid = s.currentObjectID
      if (coid.startsWith('linkedItem_') || coid.startsWith('linkedSon_')) {
        object.current = null
        return
      }
      object.current = s.items.get(coid) || (s.areas.get(coid) as TexturedSceneObjects)
      if (
        !currentTexture &&
        object.current &&
        object.current.textures &&
        object.current.textures.length > 0
      ) {
        // SOLO USAR LA ZONA "Estructura" (filtrar por zoneDisplayName === "Estructura")
        // Si no existe "Estructura", usar la primera que no sea "Patas"
        const estructuraTexture = object.current.textures.find(
          (t: any) => t.zoneDisplayName === 'Estructura',
        )
        const textureToUse =
          estructuraTexture ||
          object.current.textures.find((t: any) => t.zoneDisplayName !== 'Patas') ||
          object.current.textures[0]

        handleZoneClick(contextValue, textureToUse)
        setCurrentTexture(textureToUse)
        const inicializado = Object.fromEntries(
          object.current.textures.map((el) => [el.zoneName, 'cantPaste']),
        )
        setPasteIcons(inicializado)
      }
    })

    return () => unsubscribe()
  }, [currentTexture, contextValue])

  useEffect(() => {
    return closeModal()
  }, [closeModal])

  return <ItemZoneContext.Provider value={contextValue}>{children}</ItemZoneContext.Provider>
}

export function useObjectContext() {
  const context = useContext(ItemZoneContext)
  if (!context) throw new Error('You can only call context inside the provider')
  const editZone = useAppStore.use.editZone()
  const actions = {
    handleZoneClick: (texture: Textures[number]) => handleZoneClick(context, texture), // Click en nombre de zona
    handleGroupClick: (groupID: number) => handleGroupClick(context, groupID), // Click en aparici
    handlePaste: (e: ReactMouseEvent, zoneName: string) =>
      handlePaste(context, e, zoneName, editZone),
    handlePasteAll: () => handlePasteAll(context, editZone),
    handleTextureClick: (texture: BackAcabadosInfo) =>
      handleTextureClick(context, texture, editZone), // Aplica textura a la zona seleccionada
    handleCopy: (texture: Textures[number], e: ReactMouseEvent) => handleCopy(context, texture, e),
    refetchOnSearch: () => handleZoneClick(context, context.currentTexture, true),
  }
  return { ...context, actions }
}

export type ObjectContext = ReturnType<typeof useObjectContext>
